<script setup lang="ts">
  import type { HTMLAttributes } from "vue";
  import { computed } from "vue";
  import { cn } from "@/lib/utils";
  import { Tooltip } from "@/components/ui/tooltip";

  interface Props {
    class?: HTMLAttributes["class"];
    percentage: number;
    minTolerance?: number | null;
    minValue?: number | null;
    showMinIndicator?: boolean;
    showMinTooltip?: boolean;
    variant?: "default" | "success" | "muted";
    size?: "sm" | "md" | "lg";
    color?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    class: undefined,
    percentage: 0,
    minTolerance: null,
    minValue: null,
    showMinIndicator: true,
    showMinTooltip: true,
    variant: "default",
    size: "md",
    color: undefined,
  });

  const clampedPercentage = computed(() => Math.min(100, Math.max(0, props.percentage)));

  const indicatorPosition = computed(() => {
    if (props.minTolerance == null) return undefined;
    return `${100 - props.minTolerance}%`;
  });

  const showIndicator = computed(() => {
    return props.showMinIndicator && props.minTolerance != null && props.minTolerance > 0;
  });

  const sizeClasses = computed(() => {
    switch (props.size) {
      case "sm":
        return "h-2.5";
      case "lg":
        return "h-4";
      default:
        return "h-2.5";
    }
  });

  const tooltipText = computed(() => {
    if (props.minValue != null && props.minTolerance != null) {
      return `Min = ${props.minValue.toLocaleString("fr-FR")} (${100 - props.minTolerance}%)`;
    }
    if (props.minTolerance != null) {
      return `Min: ${100 - props.minTolerance}%`;
    }
    return "";
  });

  const bgColorClass = computed(() => {
    switch (props.variant) {
      case "success":
        return "bg-status-success";
      case "muted":
        return "bg-foreground-subtle";
      default:
        return "bg-status-info";
    }
  });
</script>

<template>
  <div
    :class="cn('relative w-full overflow-visible rounded-full bg-border', sizeClasses, props.class)"
    data-slot="progress-bar">
    <div
      :class="cn('h-full rounded-full transition-all duration-500 ease-out', !props.color && bgColorClass)"
      :style="{ width: `${clampedPercentage}%`, backgroundColor: props.color }" />
    <!-- Min indicator -->
    <template v-if="showIndicator">
      <Tooltip
        v-if="showMinTooltip"
        :content="tooltipText">
        <div
          class="absolute top-1/2 -translate-y-1/2 h-full w-[2px] bg-muted-foreground/70 cursor-help transition-all hover:bg-foreground-secondary hover:w-[3px]"
          :style="{ left: indicatorPosition }" />
      </Tooltip>
      <div
        v-else
        class="absolute top-1/2 -translate-y-1/2 h-full w-[2px] bg-muted-foreground/70"
        :style="{ left: indicatorPosition }" />
    </template>
  </div>
</template>
